import { Box, Stack, Tabs } from "@mui/material";
import { getState, getScope } from "../../src/library/dataService.ts";
import { useSignal } from "@preact/signals-react";
import { getExprValue } from "../library/interpreter.ts";
import { isNil } from "lodash-es";
import { useEffect } from "react";
import { LayoutItem, TabItem } from "../../resolvers-types";
import { containerWithPaper } from "../generators/GeneratorUtils";
import NGLayoutItem from "../generators/NGLayoutItem";
import useResizable from "../hooks/useResizable";
import { publishUserAction } from "../library/analytics";
import { setupHandlers, setupLocalState } from "../library/dataService";
import { ITabContainerProps, TabContainerVariant } from "../library/NGFieldExtensions";
import { getClassName, getTestId, getsxObject, partitionMUIClasses } from "../library/utils";
import NGTabItem from "./NGTabItem";

const tag = "NGTabContainer";

export default function NGTabContainer({ config, context }: ITabContainerProps) {

  function resolveInitialTabSelection(config: any, context: any) {
    const items = Array.isArray(config.Items)
      ? config.Items.filter((it: any) => it?.__typename === "TabItem")
      : [];

    const { state, form, parentState } = getState(context);
    const scope = getScope(context, config, state, form, {}, parentState);

    const isTabVisible = (tab: any): boolean => {
      if (tab?.Bindings?.Visible) {
        const fallback = tab?.Visible !== false;
        return Boolean(getExprValue(tab.Bindings.Visible, scope, fallback));
      }
      return tab?.Visible !== false;
    };

    const visibleItems = items.filter(isTabVisible);

    const boundSelectedId = config?.Bindings?.SelectedTabId
      ? getExprValue(config.Bindings.SelectedTabId, scope, null)
      : null;
    const boundSelectedTitle = config?.Bindings?.SelectedTabTitle
      ? getExprValue(config.Bindings.SelectedTabTitle, scope, null)
      : null;

    const explicitId = boundSelectedId ?? config?.SelectedTabId ?? null;
    const explicitTitle = boundSelectedTitle ?? config?.SelectedTabTitle ?? null;

    const pickById =
      explicitId && items.find((it: any) => it.Id === explicitId && isTabVisible(it));
    const pickByTitle =
      !pickById && explicitTitle && items.find((it: any) => it.Title === explicitTitle && isTabVisible(it));

    const chosen = pickById || pickByTitle || visibleItems[0] || null;

    return {
      id: chosen ? chosen.Id : null,
      title: chosen ? chosen.Title : null,
    };
  }

const initialSel = resolveInitialTabSelection(config, context);

  const local = setupLocalState(
    config,
    {
      Style: useSignal(config.Style ?? {}),
      Classes: useSignal(config.Classes ?? []),
      TabClasses: useSignal(config.TabClasses ?? []),
      Resizable: useSignal(config.Resizable ?? false),
      BoxStyle: useSignal(config.BoxStyle ?? {}),
      TabStyle: useSignal(config.TabStyle ?? {}),
      ContentClasses: useSignal(config.ContentClasses ?? []),
      Visible: useSignal(config.Visible ?? true),
      InReviewMode: useSignal(config.InReviewMode || false),
      SelectedTabId: useSignal(initialSel.id),
      SelectedTabTitle: useSignal(initialSel.title),

      MaxTabs: useSignal(config.MaxTabs ?? null),
    },
    context
  );

  if (config.Bindings?.SelectedTabId && local.SelectedTabId?.value == null) {
    local.SelectedTabId.value = initialSel.id;
  }
  if (config.Bindings?.SelectedTabTitle && local.SelectedTabTitle?.value == null) {
    local.SelectedTabTitle.value = initialSel.title;
  }
  const showMore = useSignal(local.MaxTabs.value ? config.Items.length > local.MaxTabs.value : false);

  const selectedTab = useSignal(0);

  useEffect(() => {
    const selectedTabId = local.SelectedTabId.value;
    const selectedTabTitle = local.SelectedTabTitle.value;
    let index = config.Items?.findIndex((item) => item.Id === selectedTabId);
    // if SelectedTabTitle is set, use it to find the index
    if (config.Bindings?.SelectedTabTitle) {
      index = config.Items?.findIndex((item) => item.Title === selectedTabTitle);
    }

    selectedTab.value = index > -1 ? index : 0;
  }, [local.SelectedTabId.value, local.SelectedTabTitle.value]);

  const handlers = setupHandlers(config, context);

  if (!isNil(config.ReviewDialogOptions)) {
    (config.ReviewDialogOptions as any).ContextId = (config as any).ContextId;
  }

  const { resizable } = useResizable(local.Resizable.value, null, config.Id);

  function renderContainer() {
    const handleChange = (e: React.SyntheticEvent, newValue: number) => {
      const { Title, Id } = config.Items[newValue] as TabItem;
      local.SelectedTabId.value = Id;
      local.SelectedTabTitle.value = Title;
      const [_, classes] = partitionMUIClasses(local.Classes.value);
      const action = publishUserAction(config, context, "tab_click", e, {
        EventCategory: "navigation",
        ElementText: Title,
        ElementClass: classes.join(" "),
      });
      if (handlers["onChange"]) {
        handlers["onChange"](e, { Index: newValue, Id, Title }, action);
      }
    };

    return (
      <Box
        sx={getsxObject(local.BoxStyle.value)}
        data-testid={getTestId(config)}
        data-type={config.__typename}
        className={getClassName(local.Classes)}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Tabs
            value={selectedTab.value}
            aria-label={config.AriaLabel ?? "tabs"}
            className={getClassName(local.TabClasses)}
            sx={getsxObject(local.Style.value)}
            TabIndicatorProps={{ sx: config.TabIndicatorStyles }}
            variant={(config.Variant as TabContainerVariant) ?? "standard"}
            {...handlers}
            onChange={handleChange}
          >
            {config.Items?.filter(
              (x, i) =>
                x.__typename == "TabItem" && (local.MaxTabs.value == null || !showMore.value || i < local.MaxTabs.value)
              // (x, i) => x.__typename == "TabItem"
            ).map((tab, key) => {
              const configOnClick = tab.Actions?.find((x: any) => x.Trigger === "onClick");
              const preHandler = (handlerName: string, action: any, e: any, data: any, formCtx: any) => {
                handleChange(e, data);
              };

              const onClick = configOnClick
                ? { ...configOnClick, preHandler: preHandler }
                : {
                    Trigger: "onClick",
                    preHandler: (handlerName: string, action: any, e: any, data: any, formCtx: any) => {
                      handleChange(e, data);
                    },
                  };

              return (
                <NGTabItem
                  key={key}
                  selected={tab.Id === local.SelectedTabId.value}
                  config={
                    {
                      ...tab,
                      TabIndex: key,
                      Style: { ...local.TabStyle.value, ...tab.Style },
                      Actions: [...((tab.Actions as any) || []), onClick],
                    } as TabItem
                  }
                  context={context}
                />
              );
            })}
            {showMore.value && (
              <NGTabItem
                key={config.Id + "more"}
                config={
                  {
                    __typename: "TabItem",
                    Id: config.Id + "more",
                    Title: config.MoreTabsText ?? "More",
                    TabIndex: local.MaxTabs.value,
                    Style: { ...local.TabStyle.value },
                    Actions: [
                      {
                        Trigger: "onClick",
                        preHandler: () => (showMore.value = !showMore.value),
                      },
                    ],
                  } as TabItem
                }
                context={context}
              />
            )}
          </Tabs>
          {config.Items?.filter((x) => x.__typename != "TabItem").map((item, key) => {
           return (<NGLayoutItem key={key} config={item as LayoutItem} context={context} />)
          }
          )}
        </Stack>
        <div className={getClassName(local.ContentClasses)}>
          {config.Items?.map((tab, tabkey) => {
            return tab.Items?.map((item, key) => {
              if (isNil(item)) {
                return <></>;
              }
              // The Box below is necessary to avoid error: "Each child in a list should have a unique "key" prop"
              return (
                <Box key={`${tabkey}-${key}`}>
                  {selectedTab.value === tabkey && <NGLayoutItem config={item} context={context} />}
                </Box>
              );
            });
          })}
        </div>
      </Box>
    );
  }

  return resizable(containerWithPaper(local, config, renderContainer, context));
}
